  import React from 'react'

interface UsePrintOptions {
  /** Elements to strip from the clone before printing (default: 'nav, button, input, label') */
  stripSelectors?: string
  /** Font size for calendar/general content (default: '12px') */
  fontSize?: string
  /** Font size specifically for table cell content (default: '12px') */
  tableFontSize?: string
}

/**
 * Reusable print hook for printing full-page content with table and calendar support.
 *
 * Usage:
 *   const { printRef, handlePrint } = usePrint()
 *   <div ref={printRef}>...content to print...</div>
 *   <button onClick={handlePrint}>Print</button>
 *
 * Handles:
 * - Stripping interactive elements (buttons, inputs) while preserving structure
 * - Proper table and calendar layout in print
 * - Responsive mobile-to-desktop conversions
 * - Print-specific styling (colors, fonts, spacing)
 */
export function usePrint(options: UsePrintOptions = {}) {
  const { stripSelectors = 'nav, button, input, label' } = options
  const printRef = React.useRef<HTMLDivElement>(null)

  const handlePrint = () => {
    const content = printRef.current
    if (!content) return

    // The Calendar Week View (marked by its existing `.calendar-compact-date`
    // root class) renders a 7-day table that is too wide for a portrait page.
    // When it is present, print in landscape so the full week fits; every other
    // print view keeps the default A4 portrait layout.
    const hasCalendarWeekView = Boolean(
      content.querySelector('.calendar-compact-date')
    )
    const pageSize = hasCalendarWeekView ? 'A4 landscape' : 'A4'

    // Clone content and strip interactive elements (but NOT table header buttons or calendar buttons)
    const clone = content.cloneNode(true) as HTMLElement

    // Drop whole sections explicitly marked as not print-friendly (e.g. an
    // interactive "Apply a Request Status" form with a live character
    // counter) — these don't make sense on a static printed page.
    clone.querySelectorAll('[data-print-exclude="true"]').forEach((el) => {
      el.remove()
    })

    clone.querySelectorAll(stripSelectors).forEach((el) => {
      // Don't remove buttons that are inside table headers or calendar
      if (el.tagName === 'BUTTON') {
        if (
          el.closest('th') ||
          el.closest('[role="dialog"]') ||
          el.closest('[class*="calendar"]') ||
          el.closest('[class*="datepicker"]') ||
          el.closest('.day') ||
          el.classList.contains('day')
        ) {
          return
        }
      }
      el.remove()
    })

    // Strip search boxes, filter inputs, etc. (but not calendar inputs)
    clone
      .querySelectorAll('input[type="search"], input[type="text"], textarea')
      .forEach((el) => {
        if (
          !el.closest('[role="dialog"]') &&
          !el.closest('[class*="calendar"]') &&
          !el.closest('[class*="datepicker"]')
        ) {
          el.remove()
        }
      })

    // Remove print button from right panel only
    clone.querySelectorAll(String.raw`.flex.justify-start.mt-4\!`).forEach((el) => {
      el.remove()
    })

    // Force desktop table visible
    clone.querySelectorAll('table').forEach((el) => {
      ;(el as HTMLElement).style.display = 'table'
    })

    // Make calendar visible
    clone
      .querySelectorAll(
        '[role="dialog"], [class*="calendar"], [class*="datepicker"]'
      )
      .forEach((el) => {
        ;(el as HTMLElement).style.display = 'block'
      })

    // Remove the Calendar Week View toolbar buttons (date-picker icon, Search
    // and the week navigation chevrons). The strip step above preserves them
    // because they live inside a [class*="calendar"] container, but they are
    // not usable on paper. Actual calendar day buttons are kept.
    clone.querySelectorAll('.calendar-compact-date button').forEach((el) => {
      if (!el.closest('.day') && !el.classList.contains('day')) {
        el.remove()
      }
    })

    // Create a temporary print container
    const printContainer = document.createElement('div')
    printContainer.id = 'temp-print-container'
    printContainer.style.padding = '20px'
    printContainer.appendChild(clone)
    document.body.appendChild(printContainer)

    // Hide everything else on the page
    const bodyChildren = Array.from(document.body.children).filter(
      (el) => el.id !== 'temp-print-container'
    ) as HTMLElement[]
    bodyChildren.forEach((el) => {
      el.style.display = 'none'
    })

    // Inject minimal print styles - only affect print container
    const style = document.createElement('style')
    style.id = 'print-only-style'
    style.innerHTML = String.raw`
      @page {
        margin: 10mm;
        size: ${pageSize};
      }
      @media print {
        #temp-print-container * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        
        #temp-print-container {
          padding: 20px !important;
          background: white !important;
        }
        
        #temp-print-container h1, #temp-print-container h2 {
          page-break-after: avoid;
        }
        
        #temp-print-container table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin: 12px 0 !important;
          page-break-inside: auto;
        }
        
        #temp-print-container tr {
          page-break-inside: avoid;
        }
        
        #temp-print-container td, #temp-print-container th {
          border: 1px solid #ddd !important;
          padding: 8px !important;
          text-align: left !important;
        }
        
        #temp-print-container th {
          background-color: #f5f5f5 !important;
          font-weight: bold !important;
        }
        
        /* Calendar wrapper - NO BORDERS */
        #temp-print-container .bg-white.border.rounded.shadow-lg {
          display: flex !important;
          flex-direction: column !important;
          width: 100% !important;
          max-width: 500px !important;
          page-break-inside: avoid !important;
          padding: 12px !important;
          background: white !important;
          height: auto !important;
          min-height: auto !important;
        }
        
        /* Calendar header */
        #temp-print-container .cal-header {
          display: block !important;
          margin-bottom: 12px !important;
          text-align: center !important;
          font-weight: bold !important;
          font-size: 16px !important;
          color: #000 !important;
          flex-shrink: 0 !important;
        }
        
        /* Calendar grid - 7 columns, auto height */
        #temp-print-container .grid.grid-cols-7 {
          display: grid !important;
          grid-template-columns: repeat(7, 1fr) !important;
          gap: 0 !important;
          width: 100% !important;
          auto-rows: minmax(50px, auto) !important;
          flex: 1 1 auto !important;
        }
        
        /* Day containers - NO BORDERS/GRID LINES */
        #temp-print-container .day {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 4px !important;
          min-height: 45px !important;
          page-break-inside: avoid !important;
        }
        
        /* Day buttons - circular with colors */
        #temp-print-container .day button {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 45px !important;
          height: 45px !important;
          min-height: 45px !important;
          border: none !important;
          padding: 0 !important;
          margin: 0 !important;
          font-size: 14px !important;
          font-weight: normal !important;
          border-radius: 50% !important;
          cursor: pointer !important;
          background-color: transparent !important;
          color: #000 !important;
        }
        
        /* Blue selected dates - circular blue background */
        #temp-print-container .day button[aria-pressed="true"] {
          background-color: #1976d2 !important;
          color: white !important;
          font-weight: bold !important;
          border-radius: 50% !important;
        }
        
        /* Green other dates - circular green background */
        #temp-print-container .day button[style*="rgb(164, 215, 81)"] {
          background-color: #7cb342 !important;
          color: black !important;
          border-radius: 50% !important;
        }
        
        /* Blue selected dates OVERRIDE - must come AFTER green rule */
        #temp-print-container .day button[aria-pressed="true"][style*="rgb(164, 215, 81)"] {
          background-color: #1976d2 !important;
          color: white !important;
          border-radius: 50% !important;
        }
        
        /* Day name headers - NO BORDERS */
        #temp-print-container .grid.grid-cols-7 > div:nth-child(-n+7) {
          font-weight: bold !important;
          text-align: center !important;
          padding: 8px !important;
          background: transparent !important;
          color: #000 !important;
          font-size: 13px !important;
        }
        
        /* Convert flex layouts to block for print - BUT NOT calendar internals */
        #temp-print-container .flex:not(.flex.flex-col):not([style*="flex-direction"]),
        #temp-print-container .flex-col:not(.cal-header):not([style*="flex"]),
        #temp-print-container .flex-row:not([style*="flex"]),
        #temp-print-container .desktop\:flex-row {
          display: block !important;
        }
        
        /* Hide non-calendar elements */
        #temp-print-container nav {
          display: none !important;
        }
        
        #temp-print-container label {
          display: none !important;
        }
        
        /* Ensure links visible */
        #temp-print-container a {
          color: #0074AD !important;
          text-decoration: underline !important;
        }
        
        /* Page break rules */
        #temp-print-container hr {
          border: 1px solid #ddd !important;
          margin: 16px 0 !important;
        }

#temp-print-container .day button[style*="linear-gradient"] {
    background: linear-gradient(
        to right,
        #a4d751 50%,
        #ffffff 50%
    ) !important;

    border: 2px solid #7cb342 !important;
    border-radius: 50% !important;
    color:black !important;
}

/* Selected partial day - same blue circle as selected dates */
#temp-print-container .day button[aria-pressed="true"][style*="linear-gradient"] {
  position: relative !important;
  overflow: hidden !important;

  background: #1976d2 !important;
  border: none !important;
  border-radius: 50% !important;

  width: 45px !important;
  height: 45px !important;

  color: white !important;
  font-weight: bold !important;
}

/* Remove partial-day split overlay */
#temp-print-container .day button[aria-pressed="true"][style*="linear-gradient"]::before {
  display: none !important;
}
        /* ---------------------------------------------------------------
           Calendar Week View print fixes (scoped to its existing
           .calendar-compact-date root so no other print view is affected).
           The on-screen desktop table lives inside an horizontal-scroll
           wrapper and hides itself below the md breakpoint, which the print
           layout treats as a narrow viewport. That caused the week to be
           clipped after Thursday and the mobile card layout to print
           instead. These rules force the full-width table to render and let
           all seven day columns fit the landscape page.
        --------------------------------------------------------------- */

        /* Drop the horizontal scroll / max-width clipping around the table. */
        #temp-print-container .calendar-compact-date .overflow-x-auto {
          overflow: visible !important;
          max-width: none !important;
          max-height: none !important;
        }

        /* Always show the desktop table (never the md:hidden mobile cards). */
        #temp-print-container .calendar-compact-date table {
          display: table !important;
          table-layout: fixed !important;
          width: 100% !important;
        }

        #temp-print-container .calendar-compact-date .md\:hidden {
          display: none !important;
        }

        /* Restore the blue header row. The generic "th" print rule above
           forces a light-grey header, which hides the calendar's white
           header text; re-apply the on-screen blue background + white text. */
        #temp-print-container .calendar-compact-date thead th {
          background-color: #003B71 !important;
          color: #ffffff !important;
        }

        /* Preserve the lighter blue used for the current day's column. */
        #temp-print-container .calendar-compact-date thead th.bg-\[\#0074AD\] {
          background-color: #0074AD !important;
        }

        /* Let every column shrink evenly so all 7 days fit the page width. */
        #temp-print-container .calendar-compact-date col,
        #temp-print-container .calendar-compact-date th,
        #temp-print-container .calendar-compact-date td {
          min-width: 0 !important;
          max-width: none !important;
          padding: 6px 4px !important;
          white-space: normal !important;
          word-break: break-word !important;
          overflow: visible !important;
          text-overflow: clip !important;
        }

        /* Give the Name column a bit more room than the seven day columns. */
        #temp-print-container .calendar-compact-date col:first-child,
        #temp-print-container .calendar-compact-date th:first-child,
        #temp-print-container .calendar-compact-date td:first-child {
          width: 20% !important;
        }

        /* Keep the calendar status legend on a single horizontal row. The
           legend uses "flex flex-col sm:flex-row"; in print the sm: breakpoint
           is inactive and the generic flex->block rule applies, so the four
           status items stack vertically. Re-flow them onto one line. This
           selector targets the legend's exact class combination only. */
        #temp-print-container .flex.flex-col.sm\:flex-row.sm\:items-center.gap-12 {
          display: flex !important;
          flex-direction: row !important;
          flex-wrap: wrap !important;
          align-items: center !important;
          gap: 16px !important;
        }

        #temp-print-container .flex.flex-col.sm\:flex-row.sm\:items-center.gap-12 > div {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          width: auto !important;
        }

        /* Hide the interactive calendar toolbar controls in print: the whole
           date picker (label, calendar icon, input and "MM/DD/YYYY" helper
           text) plus every toolbar button (Search and the week navigation
           chevrons) are not usable on paper. The Calendar Week View table has
           no buttons, so hiding all buttons here is safe. The "July 12-18 2026"
           week-range label is kept for month/year context. */
        #temp-print-container .calendar-compact-date .ds-form,
        #temp-print-container .calendar-compact-date button {
          display: none !important;
        }

        /* Center the week-range label once the nav chevrons are hidden. */
        #temp-print-container .calendar-compact-date .md\:left-1\/2 {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
          width: 100% !important;
          text-align: center !important;
        }
      }
    `
    document.head.appendChild(style)
    const originalTitle = document.title

    const fileName = `Time off Detail`

    document.title = fileName

    globalThis.addEventListener(
      'afterprint',
      () => {
        document.title = originalTitle
      },
      { once: true }
    )

    globalThis.print()
    // Restore everything after print dialog closes (print or cancel)
    bodyChildren.forEach((el) => {
      el.style.display = ''
    })
    printContainer.remove()
    document.getElementById('print-only-style')?.remove()
  }

  return { printRef, handlePrint }
}
